import { BaseApiClient } from './base'

// Response types
interface StartSessionResponse {
  success: boolean
  stream_url?: string
  book_title?: string
  translation_language?: string
  cefr_level?: string | null
  file_format?: string
  is_preview?: boolean
  preview_config?: PreviewConfig
  saved_progress?: number | null
  saved_position?: string | null
  error?: string
  error_code?: string
  downloads_used?: number
  downloads_limit?: number
}

interface PreviewConfig {
  max_percent: number
  max_pages: number
}

interface PreviewConfigResponse {
  success: boolean
  book_title?: string
  original_language?: string | null
  original_stream_url?: string | null
  original_file_format?: string
  translated_stream_url?: string
  translation_file_format?: string
  translation_language?: string
  cefr_level?: string | null
  preview_config?: PreviewConfig
  error?: string
}

interface CheckAccessResponse {
  success: boolean
  has_read: boolean
}

interface CanReadOnlineResponse {
  success: boolean
  can_read_online: boolean
  has_read: boolean
  file_format: string
}

interface TakenBook {
  id: string
  title_international: string
  author_international: string
  cover_url?: string | null
  original_language?: string | null
  format?: string | null
  genre?: string | null
  translation_id: string
  translation_language: string
  cefr_level?: string | null
  taken_at: string
  reading_progress: number
  estimated_pages?: number | null
}

interface GradedBook {
  id: string
  title_original: string
  title_international: string
  author_original: string
  author_international: string
  cover_url?: string | null
  original_language?: string | null
  format?: string | null
  available_languages: string[]
  translation_count: number
  created_at: string
}

interface GradedTranslation {
  translation_id: string
  language: string
  cefr_level?: string | null
  status: string
  file_format: string
  book_id: string
  title_international: string
  author_international: string
  cover_url?: string | null
  original_language?: string | null
  estimated_pages?: number | null
  reading_progress: number
  created_at: string
}

interface TakenBooksResponse {
  success: boolean
  books: TakenBook[]
  count: number
}

interface GradedBooksResponse {
  success: boolean
  books: GradedBook[]
  count: number
}

interface GradedTranslationsResponse {
  success: boolean
  translations: GradedTranslation[]
  count: number
}

interface UpdateProgressRequest {
  progress: number
  position?: string | null
}

interface UpdateProgressResponse {
  success: boolean
  error?: string
}

interface GetProgressResponse {
  success: boolean
  has_progress: boolean
  progress: number
  position: string | null
  last_read_at: string | null
}

export class ReaderApi extends BaseApiClient {
  constructor(baseUrl: string = '/api') {
    super(baseUrl)
  }

  /**
   * Start an online reading session.
   * This counts against download limit on first read.
   * Returns error response instead of throwing for expected errors like download limit.
   */
  async startSession(
    bookId: string, 
    translationId: string, 
    isPreview: boolean = false
  ): Promise<StartSessionResponse> {
    const url = `${this.baseUrl}/reader/start`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        book_id: bookId,
        translation_id: translationId,
        is_preview: isPreview
      })
    })

    const data = await response.json()
    
    // For 403 errors (download limit), return the error data instead of throwing
    if (response.status === 403) {
      return {
        success: false,
        error: data.error,
        error_code: data.error_code,
        downloads_used: data.downloads_used,
        downloads_limit: data.downloads_limit
      }
    }
    
    if (!response.ok) {
      throw new Error(data.error || data.message || `HTTP ${response.status}`)
    }

    return data
  }

  /**
   * Get preview configuration for side-by-side comparison.
   */
  async getPreviewConfig(
    bookId: string, 
    translationId: string, 
    skipIntro: boolean = false
  ): Promise<PreviewConfigResponse> {
    const params = new URLSearchParams()
    if (skipIntro) {
      params.set('skip_intro', 'true')
    }
    const queryString = params.toString()
    const url = `/reader/preview/${bookId}/${translationId}${queryString ? `?${queryString}` : ''}`
    return this.request(url)
  }

  /**
   * Check if user has already read a translation.
   */
  async checkAccess(translationId: string): Promise<CheckAccessResponse> {
    return this.request(`/reader/check-access/${translationId}`)
  }

  /**
   * Check if a translation can be read online (epub format).
   */
  async canReadOnline(translationId: string): Promise<CanReadOnlineResponse> {
    return this.request(`/reader/can-read-online/${translationId}`)
  }

  /**
   * Get books taken from the public library.
   */
  async getTakenBooks(): Promise<TakenBooksResponse> {
    return this.request('/user/taken-books')
  }

  /**
   * Get user's own graded/translated books (original-centric, deprecated).
   * @deprecated Use getGradedTranslations() instead
   */
  async getGradedBooks(): Promise<GradedBooksResponse> {
    return this.request('/user/graded-books')
  }

  /**
   * Get user's own graded translations (translation-centric view).
   * Each item represents a single translation that user can read.
   */
  async getGradedTranslations(): Promise<GradedTranslationsResponse> {
    return this.request('/user/graded-translations')
  }

  /**
   * Get the stream URL for reading an epub.
   * Note: This returns a URL path, not the file itself.
   */
  getStreamUrl(translationId: string): string {
    return `/api/reader/stream/${translationId}`
  }

  /**
   * Get the stream URL for the original book (for preview).
   */
  getOriginalStreamUrl(bookId: string): string {
    return `/api/reader/stream-original/${bookId}`
  }

  /**
   * Update reading progress for a book.
   */
  async updateProgress(
    translationId: string,
    progress: number,
    position?: string | null
  ): Promise<UpdateProgressResponse> {
    const body: UpdateProgressRequest = { progress }
    if (position !== undefined) {
      body.position = position
    }
    return this.request(`/reader/progress/${translationId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  /**
   * Get reading progress for a book.
   */
  async getProgress(translationId: string): Promise<GetProgressResponse> {
    return this.request(`/reader/progress/${translationId}`)
  }
}

